import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store, select } from '@ngrx/store';
import { EMPTY } from 'rxjs';
import { concatMap, map, mergeMap, switchMap } from 'rxjs/operators';
import { OrderTemplateService } from 'src/app/extensions/order-templates/services/order-template/order-template.service';

import { BasketItemsService } from 'ish-core/services/basket-items/basket-items.service';
import { BasketService } from 'ish-core/services/basket/basket.service';
import {
  MessagesPayloadType,
  displayErrorMessage,
  displaySuccessMessage,
  displayWarningMessage,
} from 'ish-core/store/core/messages';
import {
  addItemsToBasketSuccess,
  createBasketFail,
  createBasketSuccess,
  getCurrentBasketId,
} from 'ish-core/store/customer/basket';
import { getUserAuthorized } from 'ish-core/store/customer/user';
import { mapErrorToAction, mapToPayload, mapToPayloadProperty } from 'ish-core/utils/operators';

import { CustomQuickorderService } from '../../services/custom-quickorder/custom-quickorder.service';

import {
  addQuickOrderFlyOutItemsToBasket,
  addQuickOrderFlyOutItemsToBasketFail,
  addQuickOrderFlyOutItemsToBasketSuccess,
  addQuickOrderItemsToBasket,
  addQuickOrderItemsToBasketFail,
  addQuickOrderItemsToBasketSuccess,
  createQuickOrderFlyoutItems,
  createQuickOrderFlyoutItemsFail,
  createQuickOrderFlyoutItemsSuccess,
  createQuickOrderItems,
  createQuickOrderItemsFail,
  createQuickOrderItemsSuccess,
  createQuickOrderUploadItems,
  createQuickOrderUploadItemsFail,
  createQuickOrderUploadItemsSuccess,
  deleteAnonymousQuickOrder,
  deleteQuickOrder,
  deleteQuickOrderFail,
  deleteQuickOrderItems,
  deleteQuickOrderItemsFail,
  deleteQuickOrderItemsSuccess,
  deleteQuickOrderSuccess,
  loadQuickOrder,
  loadQuickOrderFail,
  loadQuickOrderSuccess,
  showQuickOrderDeleteItemsSuccessMessage,
  showQuickOrderDeleteItemsWarningMessage,
  showQuickOrderUploadItemsSuccessMessage,
  showQuickOrderUploadItemsWarningMessage,
  updateQuickOrderItem,
  updateQuickOrderItemFail,
  updateQuickOrderItemSuccess,
} from './quick-order.actions';
import {
  getQuickOrder,
  getQuickOrderID,
  getQuickOrderItemsCount,
  getQuickOrderValidations,
} from './quick-order.selectors';

@Injectable()
export class QuickOrderEffects {
  constructor(
    private actions$: Actions,
    private quickOrderService: CustomQuickorderService,
    private router: Router,
    private store: Store,
    private basketService: BasketService,
    private basketItemsService: BasketItemsService,
    private orderTemplateService: OrderTemplateService
  ) {}

  loadQuickOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadQuickOrder),
      concatMap(() =>
        this.quickOrderService.getCustomQuickorder().pipe(
          map(quickOrder => loadQuickOrderSuccess({ quickOrder })),
          mapErrorToAction(loadQuickOrderFail)
        )
      )
    )
  );

  createQuickOrderItems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createQuickOrderItems),
      mapToPayloadProperty('quickOrderItems'),
      concatMap(quickOrderItems =>
        this.quickOrderService.createCustomQuickorder(quickOrderItems).pipe(
          map(quickOrder => createQuickOrderItemsSuccess({ quickOrder })),
          mapErrorToAction(createQuickOrderItemsFail)
        )
      )
    )
  );

  createQuickOrderFlyoutItems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createQuickOrderFlyoutItems),
      mapToPayloadProperty('quickOrderItems'),
      concatMap(quickOrderItems =>
        this.quickOrderService.createCustomQuickorder(quickOrderItems).pipe(
          map(quickOrder => createQuickOrderFlyoutItemsSuccess({ quickOrder })),
          mapErrorToAction(createQuickOrderFlyoutItemsFail)
        )
      )
    )
  );

  createQuickOrderUploadItems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createQuickOrderUploadItems),
      mapToPayloadProperty('quickOrderItems'),
      concatMap(quickOrderItems =>
        this.quickOrderService.createCustomQuickorder(quickOrderItems).pipe(
          map(quickOrder => createQuickOrderUploadItemsSuccess({ quickOrder })),
          mapErrorToAction(createQuickOrderUploadItemsFail)
        )
      )
    )
  );

  showQuickOrderUploadItemsMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createQuickOrderUploadItemsSuccess),
      concatLatestFrom(() => this.store.pipe(select(getQuickOrderValidations))),
      map(([_, quickOrderErrors]) => {
        if (quickOrderErrors?.length > 0) {
          return showQuickOrderUploadItemsWarningMessage();
        } else {
          return showQuickOrderUploadItemsSuccessMessage();
        }
      })
    )
  );

  showQuickOrderUploadItemsWarningMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(showQuickOrderUploadItemsWarningMessage),
      map(
        (): MessagesPayloadType => ({
          message: 'dias.quickorder.page.upload.warning.message',
        })
      ),
      map(payload => displayWarningMessage(payload))
    )
  );

  showQuickOrderUploadItemsSuccessMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(showQuickOrderUploadItemsSuccessMessage),
      map(
        (): MessagesPayloadType => ({
          message: 'dias.quickorder.page.upload.success.message',
        })
      ),
      map(payload => displaySuccessMessage(payload))
    )
  );

  updateQuickOrderItem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateQuickOrderItem),
      mapToPayload(),
      concatMap(payload =>
        this.quickOrderService.updateCustomQuickorder(payload.itemId, payload.quickOrderItem).pipe(
          map(quickOrder => updateQuickOrderItemSuccess({ quickOrder })),
          mapErrorToAction(updateQuickOrderItemFail)
        )
      )
    )
  );

  deleteQuickOrderItems$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteQuickOrderItems),
      mapToPayloadProperty('itemIds'),
      concatMap(itemIds =>
        this.quickOrderService.deleteCustomQuickorder(itemIds).pipe(
          map(quickOrder => deleteQuickOrderItemsSuccess({ quickOrder })),
          mapErrorToAction(deleteQuickOrderItemsFail)
        )
      )
    )
  );

  showDeleteQuickOrderItemsMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteQuickOrderItemsSuccess),
      concatLatestFrom(() => this.store.pipe(select(getQuickOrderItemsCount))),
      map(([_, quickOrderItems]) => {
        if (quickOrderItems > 0) {
          return showQuickOrderDeleteItemsWarningMessage();
        } else {
          return showQuickOrderDeleteItemsSuccessMessage();
        }
      })
    )
  );

  showQuickOrderDeleteItemsWarningMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(showQuickOrderDeleteItemsWarningMessage),
      map(
        (): MessagesPayloadType => ({
          message: 'dias.quickorder.page.delete.warning.message',
        })
      ),
      map(payload => displayWarningMessage(payload))
    )
  );

  showQuickOrderDeleteItemsSuccessMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(showQuickOrderDeleteItemsSuccessMessage),
      map(
        (): MessagesPayloadType => ({
          message: 'dias.quickorder.page.delete.all.success.message',
        })
      ),
      map(payload => displaySuccessMessage(payload))
    )
  );

  navigateToQuickOrderPage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(createQuickOrderFlyoutItemsSuccess),
        concatMap(() => {
          if (!this.router.url.includes('custom-quickorder')) {
            this.router.navigate(['custom-quickorder']);
          }
          return EMPTY;
        })
      ),
    { dispatch: false }
  );

  addQuickOrderFlyOutItemsToBasket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addQuickOrderFlyOutItemsToBasket),
      mapToPayload(),
      concatLatestFrom(() => this.store.pipe(select(getCurrentBasketId))),
      concatMap(([{ items }, basketId]) => {
        if (basketId) {
          return this.basketItemsService.addItemsToBasket(items).pipe(
            mergeMap(payload => [addItemsToBasketSuccess(payload), addQuickOrderFlyOutItemsToBasketSuccess()]),
            mapErrorToAction(addQuickOrderFlyOutItemsToBasketFail)
          );
        } else {
          return this.basketService.createBasket().pipe(
            switchMap(basket =>
              this.basketItemsService.addItemsToBasket(items).pipe(
                mergeMap(payload => [
                  createBasketSuccess({ basket }),
                  addItemsToBasketSuccess(payload),
                  addQuickOrderFlyOutItemsToBasketSuccess(),
                ]),
                mapErrorToAction(addQuickOrderFlyOutItemsToBasketFail)
              )
            ),
            mapErrorToAction(createBasketFail)
          );
        }
      })
    )
  );

  addQuickOrderItemsToBasket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addQuickOrderItemsToBasket),
      mapToPayload(),
      concatLatestFrom(() => this.store.pipe(select(getCurrentBasketId))),
      concatMap(([{ items }, basketId]) => {
        if (basketId) {
          return this.basketItemsService.addItemsToBasket(items).pipe(
            mergeMap(payload => [addItemsToBasketSuccess(payload), addQuickOrderItemsToBasketSuccess()]),
            mapErrorToAction(addQuickOrderItemsToBasketFail)
          );
        } else {
          return this.basketService.createBasket().pipe(
            switchMap(basket =>
              this.basketItemsService.addItemsToBasket(items).pipe(
                mergeMap(payload => [
                  createBasketSuccess({ basket }),
                  addItemsToBasketSuccess(payload),
                  addQuickOrderItemsToBasketSuccess(),
                ]),
                mapErrorToAction(addQuickOrderItemsToBasketFail)
              )
            ),
            mapErrorToAction(createBasketFail)
          );
        }
      })
    )
  );

  dispatchDeleteQuickOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addQuickOrderItemsToBasketSuccess),
      concatLatestFrom(() => [this.store.pipe(select(getUserAuthorized)), this.store.pipe(select(getQuickOrderID))]),
      map(([_, authorized, quickOrderID]) =>
        authorized && quickOrderID ? deleteQuickOrder({ orderTemplateId: quickOrderID }) : deleteAnonymousQuickOrder()
      )
    )
  );

  deleteQuickOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteQuickOrder),
      mapToPayloadProperty('orderTemplateId'),
      mergeMap(quickOrderID =>
        this.orderTemplateService.deleteOrderTemplate(quickOrderID).pipe(
          map(() => deleteQuickOrderSuccess()),
          mapErrorToAction(deleteQuickOrderFail)
        )
      )
    )
  );

  loadNewQuickOrderAfterAddingItemsToBasket$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteQuickOrderSuccess),
      map(() => loadQuickOrder())
    )
  );

  cleanupAnonymousQuickOrder$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteAnonymousQuickOrder),
      concatLatestFrom(() => this.store.pipe(select(getQuickOrder))),
      map(([_, quickOrder]) => {
        const itemIds = quickOrder.items.reduce<string[]>((acc, item) => {
          if (item.itemLineId) {
            acc.push(item.itemLineId);
          }
          return acc;
        }, []);
        return deleteQuickOrderItems({ itemIds });
      })
    )
  );

  QuickOrderAddtoCartSuccessToast$ = createEffect(() =>
    this.actions$.pipe(
      ofType(addQuickOrderFlyOutItemsToBasketSuccess, addQuickOrderItemsToBasketSuccess),
      map(
        (): MessagesPayloadType => ({
          message: 'dias.quickorder.add.to.cart.toast.success.message',
        })
      ),
      map(payload => displaySuccessMessage(payload))
    )
  );

  QuickOrderSuccessToast$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createQuickOrderFlyoutItemsSuccess),
      map(
        (): MessagesPayloadType => ({
          message: 'dias.quickorder.page.toast.success.message',
        })
      ),
      map(payload => displaySuccessMessage(payload))
    )
  );

  QuickOrderFailureToast$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        createQuickOrderUploadItemsFail,
        createQuickOrderFlyoutItemsFail,
        addQuickOrderFlyOutItemsToBasketFail,
        addQuickOrderItemsToBasketFail
      ),
      map(
        (): MessagesPayloadType => ({
          message: 'dias.quickorder.page.toast.error.message',
        })
      ),
      map(payload => displayErrorMessage(payload))
    )
  );
}
